import { Modal } from 'antd'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { useStore } from 'store/useStore'
import { OrderKeys, regionsWithReferralCode } from 'sections/LKSection/modules/OrderModule/data'
import { Order } from 'sections/LKSection/modules/OrderModule/stores/entity/Order'
import { OrderStatus } from 'sections/LKSection/modules/OrderModule/types'
import { validatePickPointFiles } from 'sections/LKSection/shared/PickPointSharedModule/components/PickPointFilesForm/validate'
import { Margin } from 'components/ui'
import { Info } from 'components/ui/Info/Info'
import { Button, Flex, Input, Paragraph, Text, Title } from 'components/uiKit'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { LKRoutesEnum } from 'shared/constants/routes'

type OrderStatusDraftProps = {
  order: Order
}

export const OrderStatusDraft = observer(({ order }: OrderStatusDraftProps) => {
  const { viewOrderUiStore: uiStore, createOrderUiStore } = useStore()

  const navigate = useNavigate()
  const { t } = useTranslation()

  const [loadingPublish, setLoadingPublish] = useState(false)
  const [loadingDelete, setLoadingDelete] = useState(false)
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [referralCode, setReferralCode] = useState('')

  const isDeleted = Boolean(order.deleted)
  const id = order.id ? +order.id : undefined
  const isRegionWithReferralCode = regionsWithReferralCode.some(
    (region) => order?.district.includes(region) || order.region.includes(region) || order.cityName.includes(region),
  )

  const publishOrderHandler = async () => {
    if (!id) {
      return
    }

    setLoadingPublish(true)

    let assignReferralCodeResult = true

    if (referralCode.trim().length) {
      assignReferralCodeResult = await uiStore.assignReferralCode(id, referralCode)
    }

    if (!assignReferralCodeResult) {
      setLoadingPublish(false)
      return
    }

    if (validatePickPointFiles.validate(uiStore.fileList)) {
      const publishOrderResult = await createOrderUiStore.publishOrder(id)

      if (publishOrderResult?.status === OrderStatus.waitingPayment) {
        navigate(`${LKRoutesEnum.orderPayment}/${id}`)
      } else {
        navigate(`${LKRoutesEnum.orders}`)
      }

      setIsPublishModalOpen(false)
    }

    setLoadingPublish(false)
  }

  const deleteOrderHandler = async () => {
    setLoadingDelete(true)

    const res = id ? await uiStore.deleteOrder(id) : false

    if (res) {
      navigate(LKRoutesEnum.orders)
    }

    setLoadingDelete(false)
  }

  const getKey = (key: string) => `${OrderKeys.orderItemPage}.${key}`

  return (
    <>
      <Margin my={24}>
        <Title level={5}>{t(getKey('Черновик создан'))}</Title>
      </Margin>

      <Text type="secondary">{t(getKey('Заявка на добавление ПВЗ создана'))}</Text>

      <Info type="corp">
        <Text type="secondary">{t(getKey('Перед отправкой заявки ещё раз убедитесь'))}:</Text>
        <Paragraph>
          <Flex vertical gap={6}>
            <Text style={{ fontSize: 14 }}>- {t(getKey('вход на первой линии'))};</Text>
            <Text style={{ fontSize: 14 }}>- {t(getKey('на 1 этаже'))};</Text>
            <Text style={{ fontSize: 14 }}>- {t(getKey('зона для клиентов'))};</Text>
            <Text style={{ fontSize: 14 }}>- {t(getKey('зона для склада'))};</Text>
            <Text style={{ fontSize: 14 }}>- {t(getKey('круглосуточный доступ в помещение'))};</Text>
          </Flex>
        </Paragraph>
      </Info>

      <Margin mb={16}>
        <Title level={5}>{t(getKey('Что дальше?'))}</Title>
      </Margin>

      <Paragraph>{t(getKey('Подтвердите отправку заявки'))}</Paragraph>

      <Flex vertical gap={16} align="flex-start">
        <Button onClick={() => setIsPublishModalOpen(true)} disabled={isDeleted}>
          {t(getKey('Готово. Отправить заявку'))}
        </Button>

        <Button onClick={() => setIsDeleteModalOpen(true)} disabled={loadingPublish || isDeleted} danger>
          {t(getKey('Удалить заявку'))}
        </Button>
      </Flex>

      <Modal
        title={`${t(getSharedKey('Внимание'))}!`}
        open={isPublishModalOpen}
        onOk={publishOrderHandler}
        okButtonProps={{ loading: loadingPublish }}
        onCancel={() => setIsPublishModalOpen(false)}
        okText={t(getKey('Отправить заявку'))}
        cancelText={t(getSharedKey('cancel'))}
        centered
      >
        <Flex vertical gap={8}>
          <Text>{t(getKey('Заявка удаляется автоматически'))}</Text>
          {isRegionWithReferralCode && (
            <>
              <Text>{t(getKey('Для участия в реферальной программе'))}</Text>
              <Input
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value)}
                maxLength={100}
                placeholder={t(getKey('Реферальный код'))}
              />
            </>
          )}
        </Flex>
      </Modal>

      <Modal
        title={`${t(getSharedKey('Внимание'))}!`}
        open={isDeleteModalOpen}
        onOk={deleteOrderHandler}
        okButtonProps={{ disabled: loadingDelete }}
        onCancel={() => setIsDeleteModalOpen(false)}
        okText={t(getSharedKey('delete'))}
        cancelText={t(getSharedKey('cancel'))}
        centered
      >
        <Text>{t(getKey('Подтверждение удаления'))}</Text>
      </Modal>
    </>
  )
})
